import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GALLERY } from '../data/content';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

const TOTAL = GALLERY.length;
const pad = (n) => String(n + 1).padStart(2, '0');

function GalleryCard({ src, index, onOpen }) {
  return (
    <button
      onClick={() => onOpen(index)}
      className="relative shrink-0 w-[82vw] sm:w-[420px] md:w-[520px] h-[62vw] sm:h-[300px] md:h-[380px] rounded-xl overflow-hidden bg-[#EEE7D9] snap-start group"
    >
      <img
        src={src}
        alt={`Obach Rural ${index + 1}`}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <span className="absolute top-3 left-3 bg-black/45 backdrop-blur-sm text-white text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full">
        {pad(index)} / {String(TOTAL).padStart(2, '0')}
      </span>
    </button>
  );
}

function Lightbox({ openIdx, onClose, onPrev, onNext }) {
  useEffect(() => {
    if (openIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIdx, onClose, onNext, onPrev]);

  if (openIdx === null) return null;
  const stop = (e) => e.stopPropagation();

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        className="absolute top-5 right-5 text-white/90 hover:text-white z-20 p-2"
        onClick={(e) => { stop(e); onClose(); }}
        aria-label="Close"
      >
        <X size={26} />
      </button>
      <button
        className="absolute left-3 md:left-8 text-white/80 hover:text-white z-20 p-3"
        onClick={(e) => { stop(e); onPrev(); }}
        aria-label="Prev"
      >
        <ChevronLeft size={30} />
      </button>
      <button
        className="absolute right-3 md:right-8 text-white/80 hover:text-white z-20 p-3"
        onClick={(e) => { stop(e); onNext(); }}
        aria-label="Next"
      >
        <ChevronRight size={30} />
      </button>
      <img
        src={GALLERY[openIdx]}
        alt=""
        className="max-h-[86vh] max-w-[92vw] object-contain rounded-lg shadow-2xl"
        onClick={stop}
      />
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/80 text-xs tracking-widest">
        {pad(openIdx)} / {String(TOTAL).padStart(2, '0')}
      </div>
    </div>
  );
}

export default function Gallery() {
  const scrollerRef = useRef(null);
  const [openIdx, setOpenIdx] = useState(null);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const w = el.clientWidth * 0.7;
    el.scrollBy({ left: dir * w, behavior: 'smooth' });
  };

  const closeLightbox = useCallback(() => setOpenIdx(null), []);
  const nextImage = useCallback(() => setOpenIdx((i) => (i + 1) % TOTAL), []);
  const prevImage = useCallback(() => setOpenIdx((i) => (i - 1 + TOTAL) % TOTAL), []);

  return (
    <div className="relative">
      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 scroll-smooth"
        style={{ scrollbarWidth: 'thin' }}
      >
        {GALLERY.map((src, i) => (
          <GalleryCard key={src} src={src} index={i} onOpen={setOpenIdx} />
        ))}
      </div>

      <div className="hidden md:flex justify-end gap-2 mt-4">
        <button
          onClick={() => scrollBy(-1)}
          className="h-11 w-11 rounded-full border border-[#EAE4DA] bg-white hover:bg-[#F5F0E6] flex items-center justify-center text-[#2C2A26] transition-colors"
          aria-label="Prev"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          onClick={() => scrollBy(1)}
          className="h-11 w-11 rounded-full border border-[#EAE4DA] bg-white hover:bg-[#F5F0E6] flex items-center justify-center text-[#2C2A26] transition-colors"
          aria-label="Next"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <Lightbox
        openIdx={openIdx}
        onClose={closeLightbox}
        onPrev={prevImage}
        onNext={nextImage}
      />
    </div>
  );
}
